import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Layers, Home, LayoutDashboard, Headphones, Menu, Download, X, Share, HelpCircle } from 'lucide-react';
import { MobileProvider, useMobile } from './MobileContext';
import { MobileErrorBoundary } from './MobileErrorBoundary';
import { MWelcome } from './MShared';
import { MSplash, MTour, openTour } from './MTour';
import { MPushCard } from './MPush';

const InstallBanner = () => {
  const { installEvt, setInstallEvt, isIOS, isStandalone, t } = useMobile();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('azizi_install_dismissed') === '1');
  if (isStandalone || dismissed || (!installEvt && !isIOS)) return null;
  const dismiss = () => { sessionStorage.setItem('azizi_install_dismissed', '1'); setDismissed(true); };
  const install = async () => { if (!installEvt) return; installEvt.prompt(); await installEvt.userChoice; setInstallEvt(null); };
  return (
    <div className="mx-3 mt-3 rounded-2xl bg-[#0F172A] text-white p-3 flex items-center gap-3 shadow-lg" data-testid="install-banner">
      <img src="/logo-rounded.png" alt="AZIZI" className="w-10 h-10 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0 text-xs">
        {installEvt ? <p className="font-bold">{t('ثبّت تطبيق AZIZI على هاتفك', 'Install AZIZI on your phone')}</p>
          : <p className="font-bold leading-relaxed">{t('للتثبيت على آيفون: اضغط', 'On iPhone: tap')} <Share className="w-3.5 h-3.5 inline mx-0.5" /> {t('ثم "إضافة إلى الشاشة الرئيسية"', 'then "Add to Home Screen"')}</p>}
      </div>
      {installEvt && <button onClick={install} className="h-8 px-3 rounded-lg bg-[#D4AF37] text-[#0F172A] text-xs font-bold flex items-center gap-1" data-testid="install-btn"><Download className="w-3.5 h-3.5" />{t('تثبيت', 'Install')}</button>}
      <button onClick={dismiss} className="p-1 text-white/60" data-testid="install-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
};

const Shell = () => {
  const { isRTL, t, lang, setLang, isClient } = useMobile();
  const tabs = [
    { to: '/m/home', icon: Home, label: t('الرئيسية', 'Home'), id: 'home' },
    { to: '/m/support', icon: Headphones, label: t('الدعم', 'Support'), id: 'support' },
    { to: '/m', end: true, icon: Layers, label: t('الخدمات', 'Services'), id: 'services' },
    { to: '/m/dashboard', icon: LayoutDashboard, label: t('لوحة المعلومات', 'Dashboard'), id: 'dashboard' },
    { to: '/m/menu', icon: Menu, label: t('القائمة', 'Menu'), id: 'menu' },
  ];
  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-slate-100 flex flex-col max-w-md mx-auto relative" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }} data-testid="mobile-shell">
      <header className="bg-[#0F172A] text-white px-4 pb-3 flex items-center justify-between sticky top-0 z-30" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 40px)' }} data-testid="m-header">
        <div className="flex items-center gap-2">
          <img src="/logo-rounded.png" alt="AZIZI" className="w-8 h-8 rounded-lg" data-testid="m-logo" />
          <div><p className="text-sm font-bold leading-none">AZIZI.CO</p><p className="text-[10px] text-white/50 mt-0.5">{t('خدمات المعاملات', 'Transaction services')}</p></div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={openTour} className="w-7 h-7 rounded-full border border-white/20 text-white/80 flex items-center justify-center" aria-label="help" data-testid="m-help-btn"><HelpCircle className="w-4 h-4" /></button>
          <button onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="text-xs px-2.5 h-7 rounded-full border border-white/20 text-white/80" data-testid="m-lang-toggle">{lang === 'ar' ? 'EN' : 'عربي'}</button>
        </div>
      </header>
      <InstallBanner />
      <MPushCard compact />
      <MWelcome />
      <MSplash />
      <MTour />
      <main className="flex-1 pb-24 overflow-x-hidden"><Outlet /></main>
      <nav className="fixed bottom-0 inset-x-0 max-w-md mx-auto bg-white border-t border-slate-200 flex pb-[env(safe-area-inset-bottom)] z-30" data-testid="m-bottom-nav">
        {tabs.map(tb => (
          <NavLink key={tb.id} to={tb.to} end={tb.end} data-testid={`m-tab-${tb.id}`}
            className={({ isActive }) => `flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${isActive ? 'text-[#0F172A]' : 'text-slate-400'}`}>
            {({ isActive }) => (<><span className={`w-9 h-7 rounded-lg flex items-center justify-center ${isActive ? 'bg-[#0F172A]' : ''}`}><tb.icon className={`w-5 h-5 ${isActive ? 'text-[#D4AF37]' : ''}`} /></span>{tb.label}</>)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default function MobileLayout() {
  return <MobileErrorBoundary><MobileProvider><Shell /></MobileProvider></MobileErrorBoundary>;
}
